/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { transaction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LabelDto } from 'src/clu/modules/hooks/useCluUtteranceActions';
import { EntityDropdown } from 'src/core/components/EntityDropdown';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { EntityStore } from 'src/core/types/EntityStore';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { getNotFoundItem } from 'src/luis/modules/labeling/utils/entityDropdown/getNotFoundItem';
import { TokenSelectionMetadata } from 'src/luis/projects/utterances/stores/LabelingStore';
import { MenuStore } from 'src/luis/projects/utterances/stores/MenuStore';
import { Label } from 'src/modules/labeling/types/Label';
import { bracketDataAttribute, tokenDataAttribute } from 'src/modules/labeling/utils/labelingConstants';

export type CluEntityDropdownData = { target: Target; selection: Selection; cancelSelectionCB?: () => void };

export type CluEntityDropdownContainerProps = {
    isEntityPaneOpen: boolean;
    menuStore: MenuStore;
    utteranceLabels: Label[];
    entities: EntityStore[];
    onOpenEntityPane: () => void;
    tokenSelectionMetadata: TokenSelectionMetadata;
    onSetTokenSelectionMetadata: (obj: TokenSelectionMetadata) => void;
    actions: {
        onAddLabel: (id: number, label: LabelDto, method: 'menu' | 'brush' | 'createNewEntity') => void;
        onRemoveLabels: (id: number, labels: LabelDto[]) => void;
        onCreateEntity: (name: string) => void;
    };
};

const messages = defineMessages({
    createNewEntity: {
        id: 'clu.cluEntityDropdown.createNewEntity',
        defaultMessage: 'Create new entity'
    },
    noEntities: {
        id: 'clu.cluEntityDropdown.noEntities',
        defaultMessage: 'No entities found'
    },
    unlabel: {
        id: 'clu.cluEntityDropdown.unlabel',
        defaultMessage: 'Remove label'
    },
    viewInTaggingPane: {
        id: 'clu.cluEntityDropdown.viewInTaggingPane',
        defaultMessage: 'View in labeling pane'
    }
});

export const CluEntityDropdownContainer = observer((props: CluEntityDropdownContainerProps) => {
    const {
        actions,
        entities,
        menuStore,
        isEntityPaneOpen,
        onOpenEntityPane,
        tokenSelectionMetadata,
        utteranceLabels: labels = [],
        onSetTokenSelectionMetadata
    } = props;
    const { localize } = useLocalization();
    const [query, setQuery] = React.useState('');
    const { onAddLabel, onRemoveLabels, onCreateEntity } = actions;

    const selectedLabels = labels.filter(
        label => label.start <= tokenSelectionMetadata?.selection[0] && label.end >= tokenSelectionMetadata?.selection[1]
    );

    const labeledMap = new Map<string, Label>();
    selectedLabels.forEach(label => labeledMap.set(label.entity.name, label));

    useDocumentEventHandler('wheel', (e: WheelEvent) => {
        const menuContainers = document.querySelectorAll('.ms-ContextualMenu-container');

        if ([...menuContainers].some(container => container.contains(e.target as Node))) {
            return;
        }

        menuStore.resetMenuState();
    });

    const handleDismiss = (e: React.MouseEvent<HTMLElement>) => {
        if (
            (e?.type === 'click' && (e?.target as Element).hasAttribute(tokenDataAttribute)) ||
            (e?.target as Element).hasAttribute(bracketDataAttribute)
        ) {
            return;
        }

        menuStore.resetMenuState();
    };

    const handleOpenEntityPane = () => {
        transaction(() => {
            onSetTokenSelectionMetadata(null);
            menuStore.resetMenuState();
        });
        onOpenEntityPane();
    };

    const handleCreateNewEntity = () => {
        onCreateEntity(query);
        setQuery('');
    };

    let items = entities.map<IContextualMenuItem>(entity => ({
        key: entity.id,
        text: entity.name,
        iconProps: labeledMap.has(entity.id) ? { iconName: 'CheckMark' } : undefined,
        onClick: () => {
            if (labeledMap.has(entity.id)) {
                const label = labeledMap.get(entity.id);
                onRemoveLabels(tokenSelectionMetadata?.utteranceId as number, [
                    { startTokenIndex: label.start, endTokenIndex: label.end, entityId: label.entity.id as string }
                ]);
            } else {
                onAddLabel(
                    tokenSelectionMetadata?.utteranceId as number,
                    {
                        entityId: entity.id,
                        startTokenIndex: tokenSelectionMetadata?.selection[0],
                        endTokenIndex: tokenSelectionMetadata?.selection[1]
                    },
                    'menu'
                );
            }
        }
    }));

    items = substringSearchStrategy(items, query, 'text');

    const notFoundItem: IContextualMenuItem = getNotFoundItem({
        text: query,
        onClick: e => {
            e.preventDefault();
            handleCreateNewEntity();
        },
        secondaryText: localize(messages.createNewEntity)
    });

    if (items.length === 0) {
        if (query === '') {
            items.push({
                key: 'noEntities',
                secondaryText: localize(messages.noEntities)
            });
        } else {
            items.push(notFoundItem);
        }
    }

    const footerActionableItems: IContextualMenuItem[] = [];
    const labeledItems = items.filter(item => item.iconProps?.iconName === 'CheckMark');

    if (labeledItems.length) {
        footerActionableItems.push({
            key: 'unlabel',
            text: localize(messages.unlabel),
            iconProps: { iconName: 'RevToggleKey' },
            onClick: () => {
                labeledItems.forEach(item => {
                    const label = labeledMap.get(item.key);
                    onRemoveLabels(tokenSelectionMetadata.utteranceId as number, [
                        { startTokenIndex: label.start, endTokenIndex: label.end, entityId: label.entity.id.toString() }
                    ]);
                });
            }
        });
    }

    footerActionableItems.push({
        key: 'entityPalette',
        disabled: isEntityPaneOpen,
        onClick: handleOpenEntityPane,
        iconProps: { iconName: 'CustomList' },
        text: localize(messages.viewInTaggingPane)
    });

    return (
        <EntityDropdown
            query={query}
            items={items}
            onChangeQuery={setQuery}
            target={menuStore.target}
            onDismiss={handleDismiss}
            hidden={menuStore.isHidden}
            onSubmit={handleCreateNewEntity}
            footerActionableItems={footerActionableItems}
        />
    );
});
